import { useState, useMemo } from "react";
import {
  Mail,
  User,
  Building,
  Users,
  X,
  Target,
  CheckCircle2,
  Zap,
  ListTodo,
  AlertCircle,
  PauseCircle,
  Hourglass,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PriorityStatusTooltip } from "@/components/priorities/PriorityStatusTooltip";
import { PRIORITY_STATUS_LABEL } from "@/components/priorities/priorityCardTokens";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MarkdownRenderer } from "@/components/ui/MarkdownRenderer";
import { UserAvatar } from "@/components/UserAvatar";
import { useQuery } from "@tanstack/react-query";
import { priorityService } from "@/services/priorityService";
import { reportService } from "@/services/reportService";
import type { OrgNode, PriorityStatus, Priority, Report } from "@/types";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { distinctDepartment } from "@/lib/org-chart-department";

interface MemberProfileModalProps {
  member: OrgNode | null;
  isOpen: boolean;
  onClose: () => void;
  onNavigateToMember?: (memberPid: string) => void;
}

const statusConfig: Record<
  PriorityStatus,
  { label: string; icon: typeof Zap; className: string }
> = {
  focus: {
    label: PRIORITY_STATUS_LABEL.focus,
    icon: Zap,
    className: "bg-primary/10 text-primary",
  },
  queue: {
    label: PRIORITY_STATUS_LABEL.queue,
    icon: ListTodo,
    className: "bg-info/10 text-info",
  },
  done: {
    label: PRIORITY_STATUS_LABEL.done,
    icon: CheckCircle2,
    className: "bg-success/10 text-success",
  },
  blocked: {
    label: PRIORITY_STATUS_LABEL.blocked,
    icon: AlertCircle,
    className: "bg-destructive/10 text-destructive",
  },
  "on-hold": {
    label: PRIORITY_STATUS_LABEL["on-hold"],
    icon: PauseCircle,
    className: "bg-warning/10 text-warning",
  },
  waiting: {
    label: PRIORITY_STATUS_LABEL.waiting,
    icon: Hourglass,
    className: "bg-amber-500/10 text-amber-600",
  },
};

/**
 * Modal to display detailed member information
 * Shows profile, direct reports, and manager chain
 */
export function MemberProfileModal({
  member,
  isOpen,
  onClose,
  onNavigateToMember,
}: MemberProfileModalProps) {
  const [selectedReport, setSelectedReport] = useState<OrgNode | null>(null);
  const [expandedPriorityPid, setExpandedPriorityPid] = useState<string | null>(
    null
  );

  // Fetch priorities for selected direct report
  const { data: prioritiesData, isLoading: isLoadingPriorities } = useQuery({
    queryKey: ["user-priorities", selectedReport?.pid],
    queryFn: () =>
      priorityService.getPriorities({
        userPid: selectedReport?.pid,
        limit: 100,
      }),
    enabled: !!selectedReport?.pid,
  });

  // Fetch reports for selected direct report
  const { data: reportsData, isLoading: isLoadingReports } = useQuery({
    queryKey: ["user-reports", selectedReport?.pid],
    queryFn: () =>
      reportService.getReports(1, 100, {
        userPid: selectedReport?.pid,
      }),
    enabled: !!selectedReport?.pid,
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const priorities = prioritiesData?.data || [];
  const allReports = useMemo(
    () => reportsData?.data || [],
    [reportsData?.data]
  );

  if (!member) return null;

  const initials = getInitials(member.fullName);

  const renderMemberProfile = (memberData: OrgNode) => {
    const memberInitials = getInitials(memberData.fullName);
    const department = distinctDepartment(memberData.position, memberData.department);

    return (
      <div className="space-y-4">
        {/* Profile Section */}
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16 flex-shrink-0">
            <AvatarImage src={memberData.avatarUrl} alt={memberData.fullName} />
            <AvatarFallback className="bg-primary text-primary-foreground text-lg">
              {memberInitials}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg truncate">
              {memberData.fullName}
            </h3>
            {memberData.position && (
              <p className="text-sm text-muted-foreground truncate">
                {memberData.position}
              </p>
            )}
          </div>
        </div>

        <Separator />

        {/* Contact Information */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <a
              href={`mailto:${memberData.email}`}
              className="text-primary hover:underline truncate"
            >
              {memberData.email}
            </a>
          </div>

          {department && (
            <div className="flex items-center gap-3 text-sm">
              <Building className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{department}</span>
            </div>
          )}
        </div>

        <Separator />

        {/* Direct Reports */}
        {memberData.reportCount > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Users className="h-4 w-4 text-primary" />
                <span>Direct Reports ({memberData.reportCount})</span>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {memberData.directReports.map((report) => (
                  <button
                    key={report.pid}
                    onClick={() => handleReportSelect(report)}
                    className={cn(
                      "group w-full text-left rounded-lg border border-border/40 p-3 transition-all hover:border-border hover:shadow-sm",
                      selectedReport?.pid === report.pid
                        ? "border-primary/50 bg-primary/5 shadow-sm"
                        : "bg-card/50 hover:bg-card"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        src={report.avatarUrl}
                        name={report.fullName}
                        className="h-10 w-10 border-2 border-background shadow-sm"
                        fallbackClassName="bg-gradient-to-br from-primary/20 to-secondary/20 text-foreground font-semibold text-sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                          {report.fullName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {report.position}
                        </p>
                      </div>
                      {report.reportCount > 0 && (
                        <Badge
                          variant="secondary"
                          className="ml-auto flex-shrink-0 text-xs"
                        >
                          {report.reportCount}{" "}
                          {report.reportCount === 1 ? "report" : "reports"}
                        </Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const handleClose = () => {
    setSelectedReport(null);
    setExpandedPriorityPid(null);
    onClose();
  };

  const handleReportSelect = (report: OrgNode) => {
    setSelectedReport(report);
    setExpandedPriorityPid(null);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className={`${
          selectedReport ? "max-w-5xl" : "max-w-md"
        } max-h-[90vh] overflow-y-auto transition-all`}
      >
        <DialogHeader>
          <DialogTitle>Member Profile</DialogTitle>
        </DialogHeader>

        <div className={`${selectedReport ? "grid grid-cols-2 gap-6" : ""}`}>
          {/* Main Member Profile */}
          <div>{renderMemberProfile(member)}</div>

          {/* Selected Direct Report Priorities */}
          {selectedReport && (
            <div className="border-l pl-6 relative">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-sm font-semibold">
                    {selectedReport.fullName}'s Priorities
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {selectedReport.position}
                  </p>
                </div>
              </div>

              {isLoadingPriorities ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : priorities.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Target className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No priorities assigned
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {priorities.map((priority) => {
                    const status = statusConfig[priority.status || "queue"];
                    const StatusIcon = status.icon;
                    const isExpanded = expandedPriorityPid === priority.pid;
                    const priorityReports = allReports.filter(
                      (report) =>
                        report.priority?.pid === priority.pid ||
                        report.priorities?.some((p) => p.pid === priority.pid)
                    );
                    const reportCount = priorityReports.length;

                    return (
                      <div
                        key={priority.pid}
                        className="rounded-lg border bg-card overflow-hidden"
                      >
                        <button
                          onClick={() =>
                            setExpandedPriorityPid(
                              isExpanded ? null : priority.pid
                            )
                          }
                          className="w-full p-3 hover:bg-accent/50 transition-colors text-left"
                        >
                          <div className="flex items-start gap-3">
                            <Badge
                              variant={
                                priority.type === "SHP"
                                  ? "default"
                                  : "secondary"
                              }
                              className="mt-0.5 flex-shrink-0"
                            >
                              {priority.type}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">
                                {priority.name}
                              </p>
                              {priority.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                  {priority.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <PriorityStatusTooltip status={priority.status}>
                                  <Badge
                                    variant="outline"
                                    className={cn("text-xs", status.className)}
                                  >
                                    <StatusIcon className="h-3 w-3 mr-1" />
                                    {status.label}
                                  </Badge>
                                </PriorityStatusTooltip>
                                {priority.weekNumber && (
                                  <span className="text-xs text-muted-foreground">
                                    W{priority.weekNumber}
                                  </span>
                                )}
                                {reportCount > 0 && (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    {reportCount}
                                  </span>
                                )}
                              </div>
                            </div>
                            {reportCount > 0 &&
                              (isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                              ))}
                          </div>
                        </button>

                        {/* Reports List */}
                        {isExpanded && (
                          <div className="border-t bg-muted/30 px-3 py-2">
                            {isLoadingReports ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                              </div>
                            ) : reportCount === 0 ? (
                              <div className="flex flex-col items-center justify-center py-6 text-center">
                                <FileText className="h-6 w-6 text-muted-foreground/50 mb-2" />
                                <p className="text-xs text-muted-foreground">
                                  No reports for this priority
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-3">
                                  <FileText className="h-3 w-3" />
                                  <span>Reports ({reportCount})</span>
                                </div>
                                <div className="space-y-3">
                                  {priorityReports.map((report) => (
                                    <div
                                      key={report.pid}
                                      className={cn(
                                        "p-3 rounded-lg bg-background border",
                                        report.blocker &&
                                          report.blockedReason &&
                                          "border-l-4 border-l-destructive"
                                      )}
                                    >
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-medium text-muted-foreground">
                                            {format(
                                              new Date(report.reportDate),
                                              "MMM d, yyyy"
                                            )}
                                          </span>
                                          {report.blocker && (
                                            <Badge
                                              variant="destructive"
                                              className="text-xs"
                                            >
                                              <AlertCircle className="h-3 w-3 mr-1" />
                                              Blocked
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-xs text-foreground prose prose-sm max-w-none">
                                        <MarkdownRenderer
                                          content={report.content}
                                        />
                                      </div>
                                      {report.blocker &&
                                        report.blockedReason && (
                                          <div className="mt-3 pt-3 border-t-2 border-t-destructive/50">
                                            <div className="flex items-start gap-2 p-2 rounded bg-destructive/10">
                                              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                                              <div className="flex-1">
                                                <p className="text-xs font-semibold text-destructive mb-1">
                                                  Blocker Reason:
                                                </p>
                                                <p className="text-xs text-foreground">
                                                  {report.blockedReason}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
